const isBrowserEnvironment = () =>
  typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';

type IndexedDBStoreConfig = {
  dbName: string;
  version: number;
  storeName: string;
  keyPath: string;
};

// Cache DB promises by dbName + version to avoid reopening
const dbPromiseCache: Record<string, Promise<IDBDatabase>> = {};

function getDbKey(config: IndexedDBStoreConfig) {
  return `${config.dbName}::${config.version}`;
}

function openIndexedDb(config: IndexedDBStoreConfig): Promise<IDBDatabase> {
  if (!isBrowserEnvironment()) {
    return Promise.reject(
      new Error('IndexedDB is not available in this environment'),
    );
  }

  const cacheKey = getDbKey(config);

  if (!dbPromiseCache[cacheKey]) {
    dbPromiseCache[cacheKey] = new Promise((resolve, reject) => {
      const request = window.indexedDB.open(config.dbName, config.version);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(config.storeName)) {
          db.createObjectStore(config.storeName, { keyPath: config.keyPath });
        }
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        reject(request.error ?? new Error('Failed to open IndexedDB'));
      };
    });
  }

  return dbPromiseCache[cacheKey];
}

export function createIndexedDbStore<T>(config: IndexedDBStoreConfig) {
  const getDb = () => openIndexedDb(config);

  async function putRecord(record: T): Promise<void> {
    const db = await getDb();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(config.storeName, 'readwrite');
      const store = tx.objectStore(config.storeName);

      const request = store.put(record as any);

      request.onsuccess = () => resolve();
      request.onerror = () =>
        reject(request.error ?? new Error('Failed to write record'));
    });
  }

  async function deleteRecord(
    key: IDBValidKey | IDBKeyRange,
  ): Promise<void> {
    const db = await getDb();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(config.storeName, 'readwrite');
      const store = tx.objectStore(config.storeName);

      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () =>
        reject(request.error ?? new Error('Failed to delete record'));
    });
  }

  async function getRecord(
    key: IDBValidKey | IDBKeyRange,
  ): Promise<T | undefined> {
    const db = await getDb();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(config.storeName, 'readonly');
      const store = tx.objectStore(config.storeName);

      const request = store.get(key);

      request.onsuccess = () => {
        resolve(request.result as T | undefined);
      };
      request.onerror = () =>
        reject(request.error ?? new Error('Failed to read record'));
    });
  }

  return {
    putRecord,
    getRecord,
    deleteRecord,
  };
}


